
/*

 whiteboard module usage guide

 0) get Whiteboard instance

 This module exports a single function, getWhiteboard(canvasId: String): Whiteboard
 Call this function passing the id of the canvas element that is used for the Whiteboard contents rendering.
 It is guaranteed that a single Whiteboard instance is shared among different modules if the same canvasId is specified.


 1) Tools

 Whiteboard provides following methods to work with its tools

  getToolNames() returns an array of tool names usable on the Whiteboard
  getActiveToolName() returns the name of the active tool
  activateTool(toolName) changes the active tool
  getToolProperties(toolName) returns an object that holds the clone of the properties of the specified tool
  getActiveToolProperties() returns an object that holds the clone of the properties of the active tool
  setToolProperties(toolName, properties) updates the properties of the specified tool
  setActiveToolProperties(properties) updates the properties of the active tool


 2 Objects

 Whiteboard provides following methods to work with object properties

  getSelectedObjectIds() returns an array of ids of the selected objects. (NOTE: v1 does not support multiple selection)
  getObjectProperties(id) returns an object that holds the clone of the properties of the specified object
  getSelectedObjectProperties() returns an object that holds the clone of the properties of the selected objects
  setObjectProperties(id, properties) updates the properties of the specified object
  setSelectedObjectProperties(properties) updates the properties of the selected objects


 3 Documents

 Whiteboard provides following methods to manipulate its document

  getObjectById(lboId) get the object of with the specified id
  addObject(obj) add an object to the document
  removeObject(obj) remove the object from the document
  removeObjectById(lboId) remove the object with the specified id from the document

  NOTE: use of these methods should be limited to Command system


 4 Commands

 Whiteboard provides following methods to execute/undo/redo commands

   execCommand(cmd) executs the given command
   undo undo the top command on the undo-command stack
   redo redo the top command on the redo-command stack

 Following concrete commands are implemented in this version

   AddCommand(obj) adds the specified object to the document
   RemoveCommand(lboId) removes the object with the specified id from the document
   UpdateCommand(lboId, properties) applies the property updates to the object specified by the id


 5 Events

 Whiteboard emits following events

  toolChanged emitted after the active tool is changed. (event properties: toolName)
  toolPropertyUpdated emitted after tool properties are updated. (event properties: toolName, properties(updated properties of the tool))
  selectionChanged emitted after the object selection on the Whiteboard is changed. (event properties: idList, properties(properties of the selected objects))
  objectPropertyUpdated emitted after object properties are updated. (event properties: id, properties(updated properties of the object))

  NOTE: objectPropertyUpdated can be emitted on a selected object or an unselected object
        When object A and B are selected (in the next version), properties of Object A can be updated by a remote user.
        It is advised that when handling objectPropertyUpdated event, call getSelectedObjectProperties and update the property editor values
        if the id given in objectPropertyUpdated is included in the current selection.

  call Whiteboard's on method to register event listeners. (and call off method to unregister them.)

 */


//////////////////////////////////////////////////////////////////////////
// Dependencies, global variables, utilities

import {EventTarget, defineEventAttribute} from "event-target-shim"
import {fabric} from 'fabric'
fabric.Object.prototype.noScaleCache = false;
fabric.Object.prototype.lockScalingFlip = true;
fabric.Object.prototype.strokeUniform = true;
fabric.Object.prototype.cornerSize = 24
fabric.Object.prototype.borderScaleFactor = 2


const ORIGIN = location.origin.toString();
const DEVMODE = (ORIGIN.startsWith('http://localhost') || ORIGIN.startsWith('http://127.0.0.1') || ORIGIN === 'https://wb.lifesizecloudbeta.com');
const LOCALTEST = (ORIGIN.startsWith('http://localhost') || ORIGIN.startsWith('http://127.0.0.1'));
const LOCALTEST_MEETING_ID = 'MY_LOCAL_TEST';  //! Edit this line to your unique meeting id to have your private test meeting

const WHITEBOARD_URL_PROD = 'wss://wb.lifesizecloud.com/api';
const WHITEBOARD_URL_DEV = 'wss://wb.lifesizecloudbeta.com/api';

const WHITEBOARD_URL = DEVMODE ? WHITEBOARD_URL_DEV : WHITEBOARD_URL_PROD;

const g_whiteboardCache = {};
let g_localUserId = sessionStorage.getItem('__LOCALUSER__') || ('USRID' + Math.ceil(10000 * Math.random()));
let g_localUserNAME = sessionStorage.getItem('__LOCALUSERNAME__') || ('USRNAME' + Math.ceil(10000 * Math.random()));
let g_meetingId = LOCALTEST ? LOCALTEST_MEETING_ID : (sessionStorage.getItem('__MEETINGID__') || ('MTGID' + Math.ceil(10000 * Math.random())));

let g_meetingTitle = sessionStorage.getItem('__MEETINGTITLE__') || ('MTGTITLE' + Math.ceil(10000 * Math.random()));
let g_meetingExtension = sessionStorage.getItem('__MEETINGEXT__') || ('' + Math.ceil(10000 * Math.random()));;;
let g_accessCode = sessionStorage.getItem('__ACCESSCODE__');
let g_localObjId = 1;
let g_localCmdId = 1;


const VERBOSE = false;
function log(text) {
  if (VERBOSE && DEVMODE) console.log(text);
}

function clone(org) {
  return JSON.parse(JSON.stringify(org));
}

function sleep(ms) {
  return new Promise((resolve, reject) => setTimeout(resolve, ms));
}

function getParameterByName(name, url) {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

//////////////////////////////////////////////////////////////////////////
// Object definitions

const LBO_OBJECTS = {
  pen: {                         //! lbo type
    fType: 'path',               //! fabric type
    editableProperties: {        //! editable properties and their default values
      stroke: '#000000',
      strokeWidth: 2,
    },
    propMapping: {},             //! lbo property name to fabric property name mapping
  },
  rect: {
    fType: 'rect',
    editableProperties: {
      stroke: '#000000',
      strokeWidth: 3,
      fill: 'transparent',
    },
    propMapping: {},
  },
  ellipse: {
    fType: 'ellipse',
    editableProperties: {
      stroke: '#000000',
      strokeWidth: 3,
      fill: 'transparent',
    },
    propMapping: {},
  },
  line: {
    fType: 'line',
    editableProperties: {
      stroke: '#000000',
      strokeWidth: 3,
    },
    propMapping: {},
  },
  text: {
    fType: 'i-text',
    editableProperties: {
      fontFamily: 'AzoSans-Light',
      fontStyle: 'normal',
      textColor: '#000000',
      fontSize: 44,
      fill: '#f4f8b9',
      stroke: 'transparent',
      strokeWidth: 3,
      fontWeight: 'normal',
      underline: false,
      overline: false,
      linethrough: false,
    },
    propMapping: {
      textColor: 'fill',
      fill: 'backgroundColor'
    },
  },
  select: {                    //! we have select tool, but we do not have select object
    fType: '',
    editableProperties: {},
    propMapping: {},
  },
  laser: {                    //! we have select tool, but we do not have select object
    fType: 'ellipse',
    editableProperties: {
      radius: 12,
      color: '#ff0000',
    },
    propMapping: {},
  },
};


//! serialize a fabric object to a plain JSON object
function fobjToObj(fobj) {
  return fobj.toObject(['lboType', 'lboId']);
}

//! deserialize a fabric object from a plan JSON object
function objToFobj(obj) {
  let fobj = null;
  let type = obj.type;
  delete obj.type;
  switch (type) {
    case 'path':
      if (obj.path) {
        fobj = new fabric.Path(obj.path);
        delete obj.path;
      }
      break;
    case 'rect':
      fobj = new fabric.Rect();
      break;
    case 'ellipse':
      fobj = new fabric.Ellipse();
      break;
    case 'line':
      fobj = new fabric.Line();
      break;
    case 'i-text':
      if (obj.text) {
        fobj = new fabric.IText(obj.text);
        delete obj.text;
      }
      break;
    default:
      break;
  }
  if (fobj) {
    for (let key in obj) {
      fobj[key] = obj[key];
    }
  }
  return fobj;
}




//////////////////////////////////////////////////////////////////////////
// Events

class ToolChangeEvent extends Event {
  constructor(toolName) {
    super('toolChanged');
    this.toolName = toolName;
  }
}

class ToolPropertyUpdateEvent extends Event {
  constructor(toolName, properties) {
    super('toolPropertyUpdated');
    this.toolName = toolName;
    this.properties = properties;
  }
}

class SelectionChangeEvent extends Event {
  constructor(idList, properties) {
    super('selectionChanged');
    this.idList = idList;
    this.properties = properties || {};
  }
}

class ObjectPropertyUpdateEvent extends Event {
  constructor(id, properties) {
    super('objectPropertyUpdated');
    this.id = id;
    this.properties = properties;
  }
}

class CommandBufferEvent extends Event {
  constructor(status) {
    super('commandBufferEvent');
    this.status = status;
  }
}

class UsersEvent extends Event {
  constructor(users) {
    super('userListUpdated');
    this.users = users;
  }
}

class ReadyEvent extends Event {
  constructor() {
    super('ready');
  }
}

class ObjectMovingEvent extends Event {
  constructor(moving) {
    super('objectMoving');
    this.moving = moving
  }
}

class ErrorEvent extends Event {
  constructor(code, description) {
    super('error');
    this.code = code;
    this.description = description;
  }
}

//////////////////////////////////////////////////////////////////////////
// Tools

class Tool {
  constructor(name, wb) {
    this.name = name;
    this.wb = wb;
    this.propNames = Object.keys(LBO_OBJECTS[name].editableProperties) || [];
    this.propMapping = LBO_OBJECTS[name].propMapping;
    this.newObject = null;
    for (let key of this.propNames) {
      this[key] = LBO_OBJECTS[name].editableProperties[key];
    }
  }

  propertyNames() {
    return this.propNames;
  }

  applyProperties() {
    if (this.newObject) {
      for (let k of this.propertyNames()) {
        let value = this[k];
        let objKey = this.propMapping[k] || k;
        // log('toolKey = ' + k + ', objKey = ' + objKey);
        this.newObject[objKey] = this[k];
      }
    }
  }

  onActivate() {
    log(this.name + ' get activated');
  }
  onMouseDown(e) {
    log('onMouseDown is called on ' + this.name + ' at (' + e.p.x + ', ' + e.p.y + ')');
  }
  onMouseMove(e) {
    // log('onMouseMove is called on ' + this.name + ' at (' + e.p.x + ', ' + e.p.y + ')');
  }
  onMouseUp(e) {
    log('onMouseUp is called on ' + this.name + ' at (' + e.p.x + ', ' + e.p.y + ')');
  }
  onDeactivate() {
    log(this.name + ' get deactivated');
  }

  commitOperation() {}
}

class ShapeTool extends Tool {
  constructor(name, wb) {
    super(name, wb);
    this.baseX = 0; //! where mouse is pressed
    this.baseY = 0; //! where mouse is pressed
  }

  onMouseDown(e) {
    this.baseX = e.p.x;
    this.baseY = e.p.y;
    this.createNewObject();
    this.applyProperties();
    this.wb.fCanvas.add(this.newObject);
  }

  onMouseMove(e) {
    if (this.newObject) {
      let currX = e.p.x;
      let currY = e.p.y;
      let left = Math.min(this.baseX, currX);
      let top = Math.max(10, Math.min(this.baseY, currY));
      let width = Math.max(10, Math.abs(this.baseX - currX));
      let height = Math.abs(this.baseY - currY);
      this.newObject.set({ left, top, width, height });
      this.wb.fCanvas.renderAll();
    }
  }

  onMouseUp(e) {
    if (this.newObject) {
      this.newObject.setCoords();

      this.wb.fCanvas.remove(this.newObject);
      if (10 < Math.abs(this.baseX-e.p.x) && 10 < Math.abs(this.baseY-e.p.y)) {
        this.wb.execCommand(new AddCommand(this.wb, this.newObject));
      }

      this.wb.fCanvas.renderAll();
      this.newObject = null;
    }
  }
}


class SelectTool extends Tool {
  constructor(wb) {
    super('select', wb);
  }
  onActivate() {
    this.wb.fCanvas.forEachObject(obj => { obj.selectable = true });
  }
  onDeactivate() {
    this.wb.fCanvas.forEachObject(obj => { obj.selectable = false });
  }
}

class PenTool extends Tool {
  constructor(wb) {
    super('pen', wb, ['stroke', 'strokeWidth']);
    this.stroke = 'blue';
    this.strokeWidth = 3;
    this.isActive = false;
    this.commitTimer = null;
    this.wb.fCanvas.on('object:added', e => { this.onAddObjectEvent(e) });
  };

  get stroke() { return this._stroke; }
  set stroke(val) { this.wb.fCanvas.freeDrawingBrush.color = this._stroke = val; }
  get strokeWidth() { return this._strokeWidth; }
  set strokeWidth(val) { this.wb.fCanvas.freeDrawingBrush.width = this._strokeWidth = val; }

  onAddObjectEvent(e) {
    if (this.isActive) {
      // log('onAddObjectEvent is called');
      // log(e);
      if (e.target && e.target.type === 'path' && !e.target.lboType) {
        // log('It is a new stroke')
        e.target.lboStroke = true;
      }
    }
  }

  //! commit buffered strokes as a single Scribble object (group of paths)
  commitScribble() {
    // log('commitScribble is called')
    if (this.commitTimer) {
      clearTimeout(this.commitTimer);
      this.commitTimer = null;
    }

    let strokes = this.wb.fCanvas.getObjects().filter(obj => obj.lboStroke);
    if (0 < strokes.length) {
      let path = [];
      let stroke = 'black';
      let strokeWidth = 3;
      let strokeLineCap = 'round';
      let strokeLineJoin = 'round';
      for (let s of strokes) {
        path = path.concat(s.path);
        stroke = s.stroke;
        strokeLineCap = s.strokeLineCap;
        strokeLineJoin = s.strokeLineJoin;
        strokeWidth = s.strokeWidth;
        this.wb.fCanvas.remove(s);
      }

      //! adjust the coordinate with half pen-width
      const halfWidth = strokeWidth / 2;
      let minX = 999999999;
      let minY = 999999999;
      let maxX = -999999999;
      let maxY = -999999999;
      for (let pnt of path) {
        for (let i = 1; i < pnt.length; ++i) {
          pnt[i] -= halfWidth;
          if (i == 1) {
            minX = Math.min(minX, pnt[i]);
            maxX = Math.max(maxX, pnt[i]);
          } else if (i == 2) {
            minY = Math.min(minY, pnt[i]);
            maxY = Math.max(maxY, pnt[i]);
          }
        }
      }

      if (10 < maxX-minX || 10 < maxY-minY) {
        let scribble = new fabric.Path(path);
        scribble.set({fill: 'transparent', stroke, strokeWidth, strokeLineCap, strokeLineJoin})
        scribble.lboType = this.name;
        this.wb.execCommand(new AddCommand(this.wb, scribble));
      } else {
        this.wb.fCanvas.renderAll();
      }
    }
  }

  onActivate() {
    this.wb.fCanvas.isDrawingMode = true;
    this.isActive = true;
  }

  onDeactivate() {
    this.wb.fCanvas.isDrawingMode = false;
    this.isActive = false;
    this.commitScribble();
  }

  onMouseDown(e) {
    if (this.commitTimer) {
      clearTimeout(this.commitTimer);
      this.commitTimer = false;
    }
  }

  onMouseUp(e) {
    if (!this.commitTimer) {
      this.commitTimer = setTimeout(() => { this.commitScribble() } , 1000);
    }
  }

  commitOperation() {
    this.commitScribble();
  }
}

class RectTool extends ShapeTool {
  constructor(wb) {
    super('rect', wb);
  }

  createNewObject() {
    if (!this.newObject) {
      this.newObject = new fabric.Rect({
        left: this.baseX,
        top: this.baseY,
        width: 0,
        height: 0,
        selectable: false,
      });
      this.newObject.lboType = this.name;
    }
  }
}

class EllipseTool extends ShapeTool {
  constructor(wb) {
    super('ellipse', wb);
  }

  createNewObject() {
    if (!this.newObject) {
      this.newObject = new fabric.Ellipse({
        left: this.baseX,
        top: this.baseY,
        rx: 0,
        ry: 0,
        selectable: false,
      });
      this.newObject.lboType = this.name;
    }
  }

  onMouseMove(e) {
    if (this.newObject) {
      let currX = e.p.x;
      let currY = e.p.y;
      let left = Math.min(this.baseX, currX);
      let top = Math.max(10, Math.min(this.baseY, currY));
      let rx = Math.max(10, Math.abs(this.baseX - currX)) / 2;
      let ry = Math.abs(this.baseY - currY) / 2;
      this.newObject.set({ left, top, rx, ry });
      this.wb.fCanvas.renderAll();
    }
  }
}

class LineTool extends Tool {
  constructor(wb) {
    super('line', wb);
  }

  onMouseDown(e) {
    this.baseX = e.p.x;
    this.baseY = e.p.y;
    if (!this.newObject) {
      this.newObject = new fabric.Line([e.p.x, e.p.y, e.p.x, e.p.y], {
        selectable: false,
      });
      this.newObject.lboType = this.name;
      this.applyProperties();
      this.wb.fCanvas.add(this.newObject);
    }
  }

  onMouseMove(e) {
    if (this.newObject) {
      this.newObject.set({ x2: e.p.x, y2: e.p.y });
      this.wb.fCanvas.renderAll();
    }
  }

  onMouseUp(e) {
    if (this.newObject) {
      this.newObject.setCoords();

      this.wb.fCanvas.remove(this.newObject);
      if (10 < Math.abs(this.baseX-e.p.x) || 10 < Math.abs(this.baseY-e.p.y)) {
        this.wb.execCommand(new AddCommand(this.wb, this.newObject));
      }

      this.wb.fCanvas.renderAll();
      this.newObject = null;
    }
  }
}

class LaserTool extends Tool {
  constructor(wb) {
    super('laser', wb);
    this.down = false;
  }

  onMouseDown(e) {
    this.wb.putLaser(g_localUserId, e.p.x, e.p.y, this.radius, this.color);
    this.down = true;
  }

  onMouseMove(e) {
    if (this.down) {
      this.wb.putLaser(g_localUserId, e.p.x, e.p.y, this.radius, this.color);
    }
  }

  onMouseUp(e) {
    this.wb.clearLaser(g_localUserId);
    this.down = false;
  }

  onDeactivate() {
    this.wb.clearLaser(g_localUserId);
  }
}

class TextTool extends Tool {
  constructor(wb) {
    super('text', wb);
    this.isClick = false;
    this.baseX = 0;
    this.baseY = 0;
    this.clearClickTimer = null;
    this.movingObject = false;
    this.isExitingEditingText;
    this.prevLeft = 240;
    this.prevTop = 120;
  }

  onMouseDown(e) {
    this.isExitingEditingText = this.wb.isExitingEditingText;
    let pnt = new fabric.Point(e.p.x, e.p.y);
    let selectedObj = this.wb.fCanvas.getActiveObject();
    this.movingObject = (selectedObj && selectedObj.containsPoint(new fabric.Point(e.p.x, e.p.y)));
    if (this.movingObject) {
      return;
    }

    if (this.clearClickTimer) {
      this.isClick = false;
      clearTimeout(this.clearClickTimer);
      this.clearClickTimer = null;
    }
    this.isClick = true;
    this.baseX = e.p.x;
    this.baseY = e.p.y;
    this.clearClickTimer = setTimeout(() => { this.isClick = false }, 2000);
  }

  onMouseMove(e) {
    const MAX_CLICK_MOVE = 20;
    if (this.isClick) {
      if (MAX_CLICK_MOVE < Math.abs(e.p.x - this.baseX) + Math.abs(e.p.y - this.baseY)) {
        this.isClick = false;
      }
    }
  }


  onMouseUp(e) {
    if (this.clearClickTimer) {
      clearTimeout(this.clearClickTimer);
      this.clearClickTimer = null;
    }
    if (this.movingObject || this.isExitingEditingText) {
      this.movingObject = false;
      this.isExitingEditingText = false;
      return;
    }
    if (0 < this.wb.getSelectedObjectIds().length) {
      return;
    }

    let pnt = new fabric.Point(this.baseX, this.baseY);
    // let selectedObj = !this.wb.editingText() && this.isClick && this.wb.fCanvas.getObjects().find(obj => obj.containsPoint(pnt));
    let selectedObj = null; //! It is a bug that Text tool allows the user to select object, according to KAP-325
    if (this.wb.editingText()) {
      this.wb.editingText().exitEditing();
    } else if (selectedObj) {
      selectedObj.selectable = true;
      this.wb.fCanvas.setActiveObject(selectedObj);
      this.wb.fCanvas.renderAll();
    } else {
      this.addText('Type here', e.p.x, e.p.y);
    }
  }

  onDeactivate() {
    if (this.wb.editingText()){
      this.wb.editingText().exitEditing();
    }
  }

  //! special helper method to add text
  addText(text, left, top) {
    if (!left || !top) {
      left = this.prevLeft + 32;
      top = this.prevTop + 60;
      if (2400 < left || 1360 < top) {
        left = 160;
        top = 80;
      }
    }

    this.prevLeft = left;
    this.prevTop = top;

    this.newObject = new fabric.IText(text, {fontSize: LBO_OBJECTS.text.editableProperties.fontSize, left, top, selectable: false});
    this.newObject.lboType = this.name;
    this.applyProperties();
    this.newObject.setCoords();

    this.wb.addNewText(this.newObject);
    this.newObject = null;
  }
}


//////////////////////////////////////////////////////////////////////////
// Commands


class Command {
  constructor(wb, noid) {
    this.wb = wb;
    if (!noid) {
      this.cmdId = g_localUserId + ':' + g_localCmdId;
      ++g_localCmdId;
    }
    this.local = true;
  }
  exec() { return false; }
  undo() { return false; }
  toObject() { return {} }
  restoreFromObject(obj) {}

  static fromObject(wb, obj) {
    let cmd = null;
    switch (obj.type) {
      case 'add':
        cmd = new AddCommand(wb);
        break;
      case 'remove':
        cmd = new RemoveCommand(wb);
        break;
      case 'update':
        cmd = new UpdateCommand(wb);
        break;
      case 'batch':
        cmd = new BatchCommand(wb);
        break;
      default:
        break;
    }
    if (cmd) {
      cmd.restoreFromObject(obj);
      cmd.cmdId = obj.cmdId;
      cmd.local = false;
    }
    return cmd;
  }
}

class AddCommand extends Command {
  constructor(wb, obj) {
    super(wb, !obj);
    if (obj && !obj.lboId) {
      obj.lboId = g_localUserId + ':' + g_localObjId;
      ++g_localObjId;
    }
    this.obj = obj;
  }
  exec() {
    let ret = false;
    if (this.obj) {
      this.wb.addObject(this.obj);
      this.obj.setCoords();
      ret = true;
    }
    return ret;
  }
  undo() {
    return !!this.wb.removeObject(this.obj);
  }
  toObject() {
    return {
      type: 'add',
      cmdId: this.cmdId,
      obj: fobjToObj(this.obj),
    };
  }
  restoreFromObject(obj) {
    if (obj.obj) {
      this.obj = objToFobj(obj.obj);
    }
  }
}

class RemoveCommand extends Command {
  constructor(wb, lboId) {
    super(wb, !lboId);
    this.lboId = lboId;
    this.obj = null;
  }
  exec() {
    let ret = false;
    if (!this.obj) {
      this.obj = this.wb.removeObjectById(this.lboId);
      ret = true;
    }
    return ret;
  }
  undo() {
    let ret = false;
    if (this.obj) {
      this.wb.addObject(this.obj);
      this.obj = null;
      ret = true;
    }
    return ret;
  }
  toObject() {
    return {
      type: 'remove',
      cmdId: this.cmdId,
      lboId: this.lboId,
    };
  }
  restoreFromObject(obj) {
    this.lboId = obj.lboId;
  }
}

class UpdateCommand extends Command {
  constructor(wb, lboId, properties) {
    super(wb, !properties);
    this.lboId = lboId;
    this.newProperties = clone(properties || {});
    this.orgProperties = {};
  }
  exec() {
    let ret = false;
    let obj = this.wb.getObjectById(this.lboId);
    if (obj) {
      this.orgProperties = this.wb.__setObjectProperties(this.lboId, this.newProperties);
      ret = !!this.orgProperties;
    }
    return ret;
  }
  undo() {
    let ret = false;
    let obj = this.wb.getObjectById(this.lboId);
    if (obj) {
      this.newProperties = this.wb.__setObjectProperties(this.lboId, this.orgProperties);
      ret = !!this.newProperties;
    }
    return ret;
  }
  toObject() {
    return {
      type: 'update',
      cmdId: this.cmdId,
      lboId: this.lboId,
      newProperties: this.newProperties,
    };
  }
  restoreFromObject(obj) {
    this.lboId = obj.lboId;
    this.newProperties = obj.newProperties;
  }
}

class BatchCommand extends Command {
  constructor(wb, commands) {
    super(wb);
    this.commands = commands || [];
    this.undoCommands = [];
  }
  exec() {
    let ret = false;
    this.undoCommands = [];
    for (let cmd of this.commands) {
      if (cmd.exec()) {
        this.undoCommands.unshift(cmd);
        ret = true;
      }
    }
    return ret;
  }
  undo() {
    let ret = false;
    while (0 < this.undoCommands.length) {
      let cmd = this.undoCommands.pop();
      if (cmd.undo()) {
        ret = true;
      }
    }
    return ret;
  }
  toObject() {
    return {
      type: 'batch',
      cmdId: this.cmdId,
      commands: this.commands.map(cmd => cmd.toObject()),
    };
  }
  restoreFromObject(obj) {
    this.commands = obj.commands.map(obj => Command.fromObject(this.wb, obj));
    this.undoCommands = [];
  }
}



//////////////////////////////////////////////////////////////////////////
// Whiteboard

const MIXED_VALUE = Symbol();

//! NOTE: Do not use any of the data member of Whiteboard instance.
//! They will be inaccessible from outside in the future. Call methods instead.
class Whiteboard extends EventTarget {

  //! feed canvas id to construct a Whiteboard
  constructor({canvasId, meetingId, meetingTitle, userId, userName}) {
    super();
    log('Whiteboard ctor is created with ' + canvasId);
    //! this is a temporary public property of to make the migration from Adrian's code easier. Hide this from the Whiteboard users.
    this.fCanvas = new fabric.Canvas(canvasId);
    this.fCanvas.backgroundColor = 'white';
    this.page = 1; //! We work on fixed page 1
    this.meetingId = null;
    this.meetingTitle = null;
    this.userName = null;
    this.users = [];
    this.laserColorMapping = {};

    this.ws = null;
    this.wsOpen = false;  //! true when WebSocket is open
    this.connected = false;  //! true when hello/welcome handshake succeeds
    this.heartBeatTimerId = null;  //! WebSocket heart beat timer id

    this.ready = false;   //! set true when the initial document and messages are fully loaded
    this.handledSeq = 0;  //! the max seq that has been handled by this client
    this.baseMaxSeq = 0;  //! the max seq of the messages stored in the server when welcome response was sent

    this.savedTool = '';
    this.isExitingEditingText = false;
    this.laser = {};
    this.laserLabel = {};
    this.newText = null; //! We keep tentative text object when it is created. Add it to the document when the first text edit completes.

    this.trackedObjectOrgProps = []; //! Keeps [ { lbId, left, top, scaleX, scaleY, angle } ] of the tracked object. null, when no selection.

    this.undoableCommands = [];
    this.redoableCommands = [];

    this.fCanvas.renderOnAddRemove = false;
    this.fCanvas.hoverCursor = 'default';
    this.fCanvas.selection = false; //! We do not support multiple selection in v1. TODO: change this in v2
    this.fCanvas.set({
      borderColor: 'red',
      cornerColor: 'green',
      cornerSize: 6,
      transparentCorners: false
    });

    let parentElement =  document.getElementById(canvasId).parentElement;
    parentElement.style.position = 'absolute';
    parentElement.style.top = 0;
    parentElement.style.left = 0;
    parentElement.style.zIndex = 200;
    parentElement.style.overflow = 'hidden';

    let canvas = this.fCanvas;
    const tools = [
      new SelectTool(this),
      new LaserTool(this),
      new PenTool(this),
      new LineTool(this),
      new RectTool(this),
      new EllipseTool(this),
      new TextTool(this),
    ];
    this.tools = {};

    //! TODO: make all member variables private
    for (let t of tools) {
      this.tools[t.name] = t;
    }

    this.activeToolName = tools[0].name; //!


    canvas.on('before:selection:cleared', e => {
      this.isExitingEditingText = !!this.editingText();
      if (this.isExitingEditingText) {
        if (this.savedTool !== 'text') {
          this.activateTool(this.savedTool);
          this.savedTool = '';
        }
        setTimeout(() => { this.isExitingEditingText = false }, 100);
      }
    });
    canvas.on('selection:cleared', e => {
      if (this.newText) {
        this.commitNewText();
      }
      this.emitSelectionChangeEvent();
      this.trackedObjectOrgProps = [];
    });
    canvas.on('selection:updated', e => {
      this.emitSelectionChangeEvent();
      this.saveTrackedObjectProps();
    });
    canvas.on('selection:created', e => {
      this.emitSelectionChangeEvent();
      this.saveTrackedObjectProps();
    });
    canvas.on('object:modified', e => {
      if (this.newText) {
        this.commitNewText();
      } else {
        this.executeTrackCommand();
      }
    });
    canvas.on('mouse:down', e => {
      if (e.e.constructor === MouseEvent) {
        e.p = canvas.getPointer(e.e);
      }
      else if (e.e.constructor === TouchEvent) {
        if (!e.e.touches) {
          return;
        }
        e.p = canvas.getPointer(e.e.touches[0]);
      }
      if (this.getSelectedObjectIds().length > 0) {
        this.dispatchEvent(new ObjectMovingEvent(true));
      }
      this.activeTool().onMouseDown(e);
    });
    canvas.on('mouse:move', e => {
      if (e.e.constructor === MouseEvent) {
        e.p = canvas.getPointer(e.e);
      }
      else if (e.e.constructor === TouchEvent) {
        if (!e.e.touches) {
          return;
        }
        e.p = canvas.getPointer(e.e.touches[0]);
      }
      this.activeTool().onMouseMove(e)
    });
    canvas.on('mouse:up', e => {
      if (e.e.constructor === MouseEvent) {
        e.p = canvas.getPointer(e.e);
      }
      else if (e.e.constructor === TouchEvent) {
        if (!e.e.touches) {
          return;
        }
        e.p = canvas.getPointer(e.e.touches[0]);
      }
      this.dispatchEvent(new ObjectMovingEvent(false));
      this.activeTool().onMouseUp(e)
    });

    this.joinCode = getParameterByName('code');
    window.history.replaceState({}, document.title, '/');
    this.connect(); //! Connect WebSocket
  }


  addNewText(newText) {
    if (newText && newText.lboType === 'text') {
      this.newText = newText;
      this.addObject(this.newText);
      this.newText.setCoords();
      this.fCanvas.setActiveObject(this.newText);
      this.newText.enterEditing();
      this.newText.selectAll();
    }
  }

  commitNewText() {
    if (this.newText) {
      this.fCanvas.remove(this.newText);
      if (this.newText.text) {
        this.execCommand(new AddCommand(this, this.newText));
      }
      this.newText = null;
    }
  }

  editingText() {
    return (this.newText || this.fCanvas.getActiveObjects().find(obj => (obj.lboType === 'text' && obj.isEditing)));
  }

  emitSelectionChangeEvent() {
    this.dispatchEvent(new SelectionChangeEvent(this.getSelectedObjectIds(), this.getSelectedObjectProperties()));
  }

  saveTrackedObjectProps() {
    this.trackedObjectOrgProps = this.getSelectedObjectIds().map(lboId => {
      let obj = this.getObjectById(lboId);
      let props = null;
      if (obj) {
        props = {
          lboId: obj.lboId,
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          angle: obj.angle,
        };
        if (obj.hasOwnProperty('text')) {
          props.text = obj.text;
        }
      }
      return props;
    }).filter(item => !!item);
  }

  executeTrackCommand() {
    let commands = this.trackedObjectOrgProps.map(orgProps => {
      let cmd = null;
      let obj = this.getObjectById(orgProps.lboId);
      if (obj) {
        let finalProps = {
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          angle: obj.angle,
        };
        if (obj.hasOwnProperty('text')) {
          finalProps.text = obj.text;
        }
        const REMOVE_ME_LATER = false; //! Somehow invoking RemoveCommand this way causes Font name problem
        if (REMOVE_ME_LATER && orgProps.text && !finalProps.text) {
          cmd = new RemoveCommand(this, orgProps.lboId);
        } else {
          obj.set(orgProps);
          cmd = new UpdateCommand(this, orgProps.lboId, finalProps);
        }
      }
      return cmd;
    }).filter(item => !!item);
    this.execCommand(new BatchCommand(this, commands));
    this.saveTrackedObjectProps();
  }

  setObjectSelectability() {
    let selectable = (this.activeToolName === 'select');
    this.fCanvas.getObjects().forEach(obj => {
      obj.selectable = selectable;
      obj.setCoords();
    });
  }

  getObjectById(lboId) {
    return this.fCanvas.getObjects().find(obj => (obj.lboId === lboId));
  }

  addObject(obj) {
    if (!this.getObjectById(obj.lboId)) {
      this.fCanvas.add(obj);
    }
  }

  removeObject(obj) {
    let ret = null;
    if (obj.lboId) {
      ret = this.removeObjectById(obj.lboId);
    }
    return ret;
  }

  removeObjectById(lboId) {
    let obj = this.getObjectById(lboId);
    if (obj) {
      let selected = this.getSelectedObjectIds();
      if (selected && selected.includes(lboId)) {
        this.fCanvas.discardActiveObject();
      }
      this.fCanvas.remove(obj);
    }
    return obj;
  }

  setLocalIdBase() {
    let maxObjId = 0;
    let maxCmdId = 0;
    let localIdPrefix = g_localUserId+':';
    let prefixLength = localIdPrefix.length;
    this.fCanvas.getObjects().forEach(obj => {
      if (obj.lboId && obj.lboId.startsWith(localIdPrefix)) {
        maxObjId = Math.max(maxObjId, parseInt(obj.lboId.substr(prefixLength)));
      }
    });
    this.undoableCommands.forEach(cmd => {
      if (cmd.cmdId && cmd.cmdId.startsWith(localIdPrefix)) {
        maxCmdId = Math.max(maxCmdId, parseInt(cmd.cmdId.substr(prefixLength)));
      }
    });
    this.redoableCommands.forEach(cmd => {
      if (cmd.cmdId && cmd.cmdId.startsWith(localIdPrefix)) {
        maxCmdId = Math.max(maxCmdId, parseInt(cmd.cmdId.substr(prefixLength)));
      }
    });
    g_localObjId = maxObjId+1;
    g_localCmdId = maxCmdId+1;
  }

  async execCommand(command, noRendering) {
    let orgStatus = { canUndo: this.canUndo(), canRedo: this.canRedo() };

    if (command.local) {

      log('Executing a local command');

      //! locally executed command
      //! send it to the server
      await this.sendMessage({
        category: 'request',
        type: 'exec_command',
        meeting_id: this.meetingId,
        page_id: this.pageId,
        command: command.toObject(),
      });

      //! and execute it locally
      this.activeTool().commitOperation();
      command.exec(noRendering);
      this.redoableCommands = [];
      this.undoableCommands.push(command);
      this.setObjectSelectability();
      if (!noRendering) {
        this.fCanvas.renderAll();
      }
    } else {

      log('Executing a received command');
      //! command received from the server
      //! undo all commands on undoableCommands, if they are different from the received command
      //! if we find the locally executed command, just clear its local flag

      let done = false;  //! set this true when finding the same command at the top of the undo buffer

      //! undo all local commands that are overtaken by a remote command
      while (!done && 0 < this.undoableCommands.length) {
        let lastCommand = this.undoableCommands.pop();
        if (lastCommand.cmdId === command.cmdId) {
          log('The received command is identical to the last local command');
          lastCommand.local = false;
          this.undoableCommands.push(lastCommand);
          done = true;
        } else if (lastCommand.local) {
          log('Undo overtaken local command ' + lastCommand.cmdId);
          lastCommand.undo();
        } else {
          log('No more local command that can be overtaken. Breaking the loop.');
          this.undoableCommands.push(lastCommand);
          break;
        }
      }
      if (!done) {
        log('Executing a remote command ' + command.cmdId);
        // log(JSON.stringify(command.toObject(), null, 2));
        command.exec(noRendering);
        this.redoableCommands = [];
        this.undoableCommands.push(command);
        this.setObjectSelectability();
        if (!noRendering) {
          this.fCanvas.renderAll();
        }
      } else {
        log('done flag is true');
      }
    }

    let status = { canUndo: this.canUndo(), canRedo: this.canRedo() };
    if (orgStatus.canUndo !== status.canUndo || orgStatus.canRedo !== status.canRedo) {
      this.dispatchEvent(new CommandBufferEvent(status));
    }
  }

  _clear() {
    let orgStatus = { canUndo: this.canUndo(), canRedo: this.canRedo() };

    this.undoableCommands = [];
    this.redoableCommands = [];
    this.fCanvas.remove(...this.fCanvas.getObjects());

    this.setObjectSelectability();
    this.fCanvas.renderAll();

    let status = { canUndo: this.canUndo(), canRedo: this.canRedo() };
    if (orgStatus.canUndo !== status.canUndo || orgStatus.canRedo !== status.canRedo) {
      this.dispatchEvent(new CommandBufferEvent(status));
    }
  }

  sendClearRequestImpl() {
    if (this.undoableCommands.find(cmd => cmd.local)) {
      //! Loop-back command has not come back yet. Keeps retrying until it comes back
      setTimeout(() => { this.sendClearRequestImpl() }, 100);
    } else {
      this.sendMessage({
        category: 'request',
        type: 'clear',
        meeting_id: this.meetingId,
        page_id: this.pageId,
      });
    }
  }

  clearHistory() {
    this.activeTool().commitOperation();
    this.sendClearRequestImpl();
  }

  sendUndRequestImpl() {
    if (this.undoTarget) {
      let command = this.undoableCommands.find(cmd => cmd.cmdId === this.undoTarget);
      if (command) {
        if (command.local) {
          //! Loop-back command has not come back yet. Keeps retrying until it comes back
          setTimeout(() => { this.sendUndRequestImpl() }, 100);
        } else {
          //! All users should have the command by now. Send the UNDO request.
          this.sendMessage({
            category: 'request',
            type: 'undo',
            meeting_id: this.meetingId,
            page_id: this.pageId,
            undoCmdId: this.undoTarget,
          });
          this.undoTarget = null;
        }
      } else {
        this.undoTarget = null;
      }
    }
  }

  undo() {
    this.activeTool().commitOperation();
    if (0 < this.undoableCommands.length && !this.undoTarget) {
      this.undoTarget = this.undoableCommands[this.undoableCommands.length-1].cmdId;
      this.sendUndRequestImpl();
    }
  }

  redo() {
    this.activeTool().commitOperation();
    if (0 < this.redoableCommands.length) {
      let redoCmdId = this.redoableCommands[this.redoableCommands.length-1].cmdId;
      this.sendMessage({
        category: 'request',
        type: 'redo',
        meeting_id: this.meetingId,
        page_id: this.pageId,
        redoCmdId,
      });
    }
  }

  _undo(undoCmdId) {
    if (!this.undoableCommands.find(cmd => (cmd.cmdId === undoCmdId))) {
      return;
    }

    let orgStatus = { canUndo: this.canUndo(), canRedo: this.canRedo() };

    while (0 < this.undoableCommands.length) {
      let command = this.undoableCommands.pop();
      if (command) {
        command.undo();
        this.redoableCommands.push(command);
        if (command.cmdId === undoCmdId) {
          break;
        }
      }
    }
    this.setObjectSelectability();
    this.fCanvas.renderAll();

    let status = { canUndo: this.canUndo(), canRedo: this.canRedo() };
    if (orgStatus.canUndo !== status.canUndo || orgStatus.canRedo !== status.canRedo) {
      this.dispatchEvent(new CommandBufferEvent(status));
    }
  }

  _redo(redoCmdId) {
    if (!this.redoableCommands.find(cmd => (cmd.cmdId === redoCmdId))) {
      return;
    }

    let orgStatus = { canUndo: this.canUndo(), canRedo: this.canRedo() };

    while (0 < this.redoableCommands.length) {
      let command = this.redoableCommands.pop();
      if (command) {
        command.exec();
        this.undoableCommands.push(command);
        if (command.cmdId === redoCmdId) {
          break;
        }
      }
    }
    this.setObjectSelectability();
    this.fCanvas.renderAll();

    let status = { canUndo: this.canUndo(), canRedo: this.canRedo() };
    if (orgStatus.canUndo !== status.canUndo || orgStatus.canRedo !== status.canRedo) {
      this.dispatchEvent(new CommandBufferEvent(status));
    }
  }

  canUndo() {
    return (0 < this.undoableCommands.length);
  }

  canRedo() {
    return (0 < this.redoableCommands.length);
  }

  removeSelectedObjects() {
    this.activeTool().commitOperation();
    let commands = this.getSelectedObjectIds().map(id => new RemoveCommand(this, id));
    this.execCommand(new BatchCommand(this, commands));
  }

  removeAllObjects() {
    this.activeTool().commitOperation();
    let allIds = this.fCanvas.getObjects().map(obj => obj.lboId).filter(id => !!id);
    let commands = allIds.map(id => new RemoveCommand(this, id));
    this.execCommand(new BatchCommand(this, commands));
  }

  on(eventType, callback) {
    this.addEventListener(eventType, callback);
  }

  off(eventType, callback) {
    this.removeEventListener(eventType, callback);
  }

  getToolNames() {
    return Object.keys(this.tools);
  }

  getActiveToolName() {
    return this.activeToolName;
  }

  activeTool() {
    return this.tools[this.activeToolName];
  }

  activateTool(toolName) {
    if (this.activeToolName !== toolName && this.getToolNames().includes(toolName)) {
      this.activeTool().commitOperation();
      this.activeTool().onDeactivate();
      this.activeToolName = toolName;
      this.activeTool().onActivate();
      this.fCanvas.discardActiveObject().renderAll();
      this.dispatchEvent(new ToolChangeEvent(toolName));
    }
  }

  getToolProperties(toolName) {
    let ret = null;
    if (this.getToolNames().includes(toolName)) {
      ret = {};
      for (let key of this.tools[toolName].propertyNames()) {
        ret[key] = this.tools[toolName][key];
      }
    }
    return ret;
  }

  getActiveToolProperties() {
    return this.getToolProperties(this.getActiveToolName());
  }

  setToolProperties(toolName, properties) {
    this.activeTool().commitOperation();
    if (this.getToolNames().includes(toolName)) {
      let keys = this.tools[toolName].propertyNames();
      let updatedProperties = null;
      for (let key in properties) {
        if (keys.includes(key) && this.tools[toolName][key] !== properties[key]) {
          updatedProperties = updatedProperties || {};
          updatedProperties[key] = this.tools[toolName][key] = properties[key];
        }
      }
      if (updatedProperties) {
        this.dispatchEvent(new ToolPropertyUpdateEvent(toolName, updatedProperties));
      }
    }
    if (toolName === 'laser') { //! ugly hack to emit user event when laser color is updated
      this.dispatchEvent(new UsersEvent(this.getUsers()));
    }
  }

  setActiveToolProperties(properties) {
    this.setToolProperties(this.getActiveToolName(), properties);
  }

  getSelectedObjectIds() {
    return this.fCanvas.getActiveObjects().map(obj => obj.lboId).filter(id => !!id);
  }

  getObjectProperties(lboId) {
    let ret = null;
    let obj = this.getObjectById(lboId);
    if (obj && obj.lboType) {
      ret = {};
      let propMapping = LBO_OBJECTS[obj.lboType].propMapping;
      for (let key in LBO_OBJECTS[obj.lboType].editableProperties) {
        let fabKey = propMapping[key] || key;
        ret[key] = obj[fabKey];
      }
    }
    return ret;
  }

  getSelectedObjectProperties() {
    let ret = {};
    for (let lboId of this.getSelectedObjectIds()) {
      let props = this.getObjectProperties(lboId);
      for (let key in props) {
        if (ret.hasOwnProperty(key)) {
          if (ret[key] !== props[key]) {
            ret[key] = MIXED_VALUE;
          }
        } else {
          ret[key] = props[key];
        }
      }
    }
    return ret;
  }

  //! apply the property changes to the fabric object
  //! This method should not be called from the UI layer. Internal use only.
  __setObjectProperties(lboId, properties) {
    let orgProps = null;
    let obj = this.getObjectById(lboId);
    if (obj && obj.lboType) {
      let allOrgProps = this.getObjectProperties(lboId);
      let propMapping = LBO_OBJECTS[obj.lboType].propMapping;
      for (let key in properties) {
        if (allOrgProps.hasOwnProperty(key)) {
          let fabKey = propMapping[key] || key;
          if (obj[fabKey] !== properties[key]) {
            orgProps = orgProps || {};
            orgProps[key] = obj[fabKey];
            obj[fabKey] = properties[key];
            obj.dirty = true;
          }
        } else {
          if (obj[key] !== properties[key]) {
            orgProps = orgProps || {};
            orgProps[key] = obj[key];
            obj[key] = properties[key];
          }
        }
      }
    }
    return orgProps;
  }

  setSelectedObjectProperties(properties) {
    this.activeTool().commitOperation();
    let commands = this.getSelectedObjectIds().map(id => new UpdateCommand(this, id, properties));
    this.execCommand(new BatchCommand(this, commands));
    this.fCanvas.renderAll();
  }

  putLaser(userId, x, y, r, color) {
    if (!color) color = '#ff00000'
    //! Daniel, please use this color when rendering the laser
    if (userId === g_localUserId) {
      let now = new Date().getTime();
      this.lastLaserPostTime = this.lastLaserPostTime || 0;
      if (15 < now - this.lastLaserPostTime) {
        this.sendMessage({
          category: 'request',
          type: 'show_laser',
          meeting_id: this.meetingId,
          page_id: this.pageId,
          x,
          y,
          r,
          c: color,
          userId
        });
        this.lastLaserPostTime = now;
      }
    }
    if (this.laser[userId]) {
      this.laser[userId].set({
        left: x - r,
        top: y - r,
      });
      this.laserLabel[userId].set({
        left: x + r + 5,
        top: y - r,
      });
    } else {
      var shadow = new fabric.Shadow({
        color: color,
        blur: 60
      });
      this.laser[userId] = new fabric.Ellipse({
        left: x - r,
        top: y - r,
        rx: r,
        ry: r,
        stroke: color,
        fill: color,
        selectable: false,
        opacity:0.5,
        shadow
      });
      this.fCanvas.add(this.laser[userId]);
      let userName = this.users.find(u => u.user_id === userId).user_name || userId;
      this.laserLabel[userId] = new fabric.IText(userName.split(' ')[0], {
        left: x + r + 5,
        top: y - r,
        selectable: false,
        opacity:0.5,
        fontFamily: 'AzoSans-Light',
        backgroundColor: color,
        fontSize: 22,
        fill: '#fff'
      });
      this.fCanvas.add(this.laserLabel[userId]);
      let t = 0;
      this.laser[userId].lboBlinkTimer = setInterval(() => {
        t += Math.PI / 30;
        let v = (Math.cos(t) + 1.0) / 4; //! v moves between (0 and 0.5)
        this.laser[userId].dirty = true;
        // this.laser[userId].opacity = v;
        this.fCanvas.renderAll();
      }, 30);
      if (this.laser[userId].lboClearTimer) {
        clearTimeout(this.laser[userId].lboClearTimer);
        this.laser[userId].lboClearTimer = null;
      }
      this.laser[userId].lboClearTimer = setTimeout(() => {
        this.laser[userId].lboClearTimer = null;
        this.clearLaser(userId);
      }, 15000);
    }
  }

  clearLaser(userId) {
    if (this.laser[userId]) {
      if (userId === g_localUserId) {
        this.sendMessage({
          category: 'request',
          type: 'hide_laser',
          meeting_id: this.meetingId,
          page_id: this.pageId,
          userId
        });
        this.lastLaserPostTime = 0;
      }
      clearInterval(this.laser[userId].lboBlinkTimer);
      this.laser[userId].lboBlinkTimer = null;
      if (this.laser[userId].lboClearTimer) {
        clearTimeout(this.laser[userId].lboClearTimer);
        this.laser[userId].lboClearTimer = null;
      }

      this.fCanvas.remove(this.laserLabel[userId]);
      this.fCanvas.remove(this.laser[userId]);
      this.fCanvas.renderAll();
      this.laser[userId] = null;
    }
  }

  getLocalUser() {
    return { user_id: g_localUserId, user_name: this.userName };
  }

  getUsers() {
    return this.users.map(user => {
      return {
        user_id: user.user_id,
        user_name: user.user_name,
        laser_color: (this.laserColorMapping[user.user_id] || (user.user_id === g_localUserId ? this.tools['laser'].color : LBO_OBJECTS.laser.editableProperties.color)),
      }
    });
  }

  getTitle() {
    return this.meetingTitle;
  }

  getExtension() {
    return this.meetingExtension;
  }

  ////////////////////////////////////////////////////////////////////////
  // WebSocket related methods
  connect() {
    this.close();
    this.ws = new WebSocket(WHITEBOARD_URL);
    this.ws.onopen = () => this.onWsOpen();
    this.ws.onmessage = event => this.onWsMessage(event);
    this.ws.onclose = () => this.onWsClose();
    this.ws.onerror = err => this.onWsError(err);
  }

  //! recovery from unexpected web socket connection loss during a session
  reconnect() {
    const RECOVERY_BY_RELOADING = true;
    if (RECOVERY_BY_RELOADING) {
      window.location.reload();
    } else {
      this.clearWatchdog();
      if (this.connected) {
        setTimeout(() => {
          log('auto-recovery');
          //! detach handlers from the old websocket
          const nop = () => {};
          this.ws.onopen = nop;
          this.ws.onmessage = nop;
          this.ws.onclose = nop;
          this.ws.onerror = nop;

          //! setup new websocket
          this.ws = new WebSocket(WHITEBOARD_URL);
          this.ws.onopen = () => this.onWsOpen();
          this.ws.onmessage = event => this.onWsMessage(event);
          this.ws.onclose = () => this.onWsClose();
          this.ws.onerror = err => this.onWsError(err);
        }, 500);
        this.setWatchdog();
      }
    }
  }

  clearWatchdog() {
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  setWatchdog() {
    this.clearWatchdog();
    this.watchdogTimer = setTimeout(() => {
      log('VOW-WOW (WatchDog)')
      this.clearWatchdog();
      this.reconnect();
      this.setWatchdog();
    }, 60 * 60 * 1000); //! 1 hour
  }

  close() {
    this.stopHeartBeat();
    this.clearWatchdog();
    if (this.ws) {
      if (this.connected) {
        let req = {
          category: 'request',
          type: 'bye',
          meetingId: this.meetingId,
        };
        this.sendMessage(req);
        this.connected = false;
      }
      let wsToClose = this.ws;
      this.ws = null;
      setTimeout(()=>{wsToClose.close()}, 3000);
    }
  }

  onWsOpen() {
    log('Connection Opened');
    this.clearWatchdog();
    this.wsOpen = true;
    this.sendHello();
  }

  sendHello() {
    if (this.wsOpen) {
      let req = {
        category: 'request',
        type: 'hello',
        version: 1,
        user_id: g_localUserId,
        code: this.joinCode,
      };
      if (this.joinCode) {
        req.code = this.joinCode;
      } else {
        req.meeting_id = g_meetingId;
        req.user_id = g_localUserId;
        req.user_name = g_localUserNAME;
        req.meeting_title = g_meetingTitle;
        req.access_code = g_accessCode;
      }
      this.sendMessage(req);
    }
  }

  stopHeartBeat() {
    if (this.heartBeatTimerId) {
      clearTimeout(this.heartBeatTimerId);
      this.heartBeatTimerId = null;
    }
  }

  startHeartBeat() {
    this.stopHeartBeat();
    if (this.connected) {
      this.heartBeatTimerId = setTimeout(() => this.sendMessage({category: 'keep_alive', meeting_id: this.meetingId}), 30000);
    }
  }

  onWsClose() {
    log('WS connection closed');
    if (this.connected) {  //! this.connected
      this.reconnect();
      return;
    }
    this.stopHeartBeat();
    this.clearWatchdog();
  }

  onWsError(err) {
    log('WebSocket Error');
    log(err);
    this.onWsClose();
  }

  onWsMessage(event) {
    let msg = JSON.parse(event.data);
    if (msg.category === 'debug') {
      log('SVR-DBG:' + msg.msg);
    }
    this.setWatchdog();
    log(msg);

    this.handleMessage(msg);
  }

  setReady() {
    this.ready = true;
    this.setLocalIdBase();
    this.dispatchEvent(new UsersEvent(this.getUsers()));
    this.fCanvas.renderAll();
    this.dispatchEvent(new ReadyEvent());

    window.addEventListener('keydown', e => {
      this.onKeyDown(e);
    })
  }

  onKeyDown(e) {
    if (this.editingText()) return;

    if (!e.altKey && !e.ctrlKey && !e.metaKey && e.key.length === 1) {
      if (typeof this.tools['text'].addText === 'function') {
        if (this.activeToolName !== 'text') {
          this.savedTool = this.activeToolName;
          this.activateTool('text');
        }
        this.tools['text'].addText(e.key);
      }
    } else {
      let now = new Date().getTime();
      let keyState = '';
      if (e.ctrlKey || e.metaKey) keyState += 'ctrl_';
      if (e.altKey) keyState += 'alt_';
      if (e.shiftKey) keyState += 'shift_';
      keyState += e.key;
      if (this.handledKeyState === keyState && now-this.keyHandledTime < 300) {
        log('DEBOUNCE BLOCK!!');
        return;
      }

      switch (keyState) {
        case 'ctrl_z':
          this.undo();
          break;
        case 'ctrl_shift_z':
        case 'ctrl_y':
          this.redo();
          break;
        case 'Delete':
        case 'Backspace':
          this.removeSelectedObjects();
          break;
        case 'Enter':
          {
            let sel = this.getSelectedObjectIds();
            if (sel.length === 1) {
              let obj = this.getObjectById(sel[0]);
              if (obj && obj.lboType === 'text') {
                setTimeout(()=> {
                  obj.enterEditing();
                  obj.selectAll();
                }, 50);
              }
            }
          }
          break;
        default:
          break;
      }

      this.handledKeyState = keyState;
      this.keyHandledTime = now;
    }
  }

  isReady() {
    return this.ready;
  }

  async handleMessage(msg, noRendering) {
    log('handling ' + msg.type + (msg.seq ? ` [${msg.seq}]`: ''));
    switch (msg.type) {

      case 'chunk':
      {
        this.chunks = this.chunks || {};
        this.chunks[msg.frameid] = this.chunks[msg.frameid] || new Array(msg.chunk_count);
        this.chunks[msg.frameid][msg.chunk_index] = msg.payload;
        let complete = true;
        for (let i = 0; complete && i < msg.chunk_count; ++i) {
          if (!this.chunks[msg.frameid][i]) {
            complete = false;
          }
        }
        if (complete) {
          let frame = '';
          for (let i = 0; complete && i < msg.chunk_count; ++i) {
            frame += this.chunks[msg.frameid][i];
          }
          delete this.chunks[msg.frameid];
          await this.handleMessage(JSON.parse(frame), noRendering);
        }
      }
        break;

      case 'welcome':
      {
        this.connected = true;
        g_meetingId = this.meetingId = msg.meeting_id;
        sessionStorage.setItem('__MEETINGID__', g_meetingId);
        this.pageId = this.meetingId + ':' + this.page;
        g_meetingTitle = this.meetingTitle = msg.meeting_title;
        sessionStorage.setItem('__MEETINGTITLE__', g_meetingTitle);
        g_meetingExtension = this.meetingExtension = msg.meeting_extension;
        sessionStorage.setItem('__MEETINGEXT__', g_meetingExtension);
        g_localUserId = msg.user_id;
        sessionStorage.setItem('__LOCALUSER__', g_localUserId);
        g_accessCode = msg.access_code;
        sessionStorage.setItem('__ACCESSCODE__', g_accessCode);
        g_localUserNAME = this.userName = (msg.user_name || g_localUserNAME);
        sessionStorage.setItem('__LOCALUSERNAME__', g_localUserNAME);
        this.users = msg.users;
        this.handledSeq = msg.base_seq;
        this.baseMaxSeq = msg.max_seq;
        log('Before processing the messages in welcome message');
        log('this.handledSeq = ' + this.handledSeq);
        log('this.baseMaxSeq = ' + this.baseMaxSeq);

        for (let m of msg.messages) {
          if (this.handledSeq < m.seq) {
            await this.handleMessage(m, true);
            this.handledSeq = m.seq;
          }
        }
        log('After processing the messages in welcome message');
        log('this.handledSeq = ' + this.handledSeq);
        log('this.baseMaxSeq = ' + this.baseMaxSeq);
        //! We need to keep sending 'messages' request until handledSeq catch up with baseMaxSeq
        if (this.baseMaxSeq <= this.handledSeq || !msg.messages.length) {
          this.setReady();
        } else {
          await this.sendMessage({
            category: 'request',
            type: 'messages',
            page_id: this.pageId,
            base_seq: this.handledSeq,
          });
        }
      }
        break;

      case 'messages':
      {
        for (let m of msg.messages) {
          if (this.handledSeq < m.seq) {
            await this.handleMessage(m, true);
            this.handledSeq = m.seq;
          }
        }
        //! We need to keep sending 'messages' request until handledSeq catch up with baseMaxSeq
        if (this.baseMaxSeq <= this.handledSeq || !msg.messages.length) {
          this.setReady();
        } else {
          await this.sendMessage({
            category: 'request',
            type: 'messages',
            page_id: this.pageId,
            base_seq: this.handledSeq,
          });
        }
      }
        break;

      case 'show_laser':
      {
        let colorUpdated = (this.laserColorMapping[msg.userId] !== msg.c);
        this.laserColorMapping[msg.userId] = msg.c;
        if (colorUpdated) {
          this.dispatchEvent(new UsersEvent(this.getUsers()));
        }
        if (msg.userId !== g_localUserId) {
          this.putLaser(msg.userId, msg.x, msg.y, msg.r, msg.c);
        }
      }
        break;

      case 'hide_laser':
      {
        if (msg.userId !== g_localUserId) {
          this.clearLaser(msg.userId);
        }
      }
        break;

      case 'exec_command':
      {
        // log('Received a command\n' + JSON.stringify(msg.command));
        let command = Command.fromObject(this, msg.command);
        if (command) {
          await this.execCommand(command, noRendering);
        }
      }
        break;

      case 'undo':
        this._undo(msg.undoCmdId);
        break;

      case 'redo':
        this._redo(msg.redoCmdId);
        break;

      case 'clear':
        this._clear();
        break;

      case 'users':
      {
        this.users = msg.users;
        this.dispatchEvent(new UsersEvent(this.getUsers()));
      }
        break;

      case 'ok':
        break;

      case 'error':
        log('Server side error: ' + msg.code + ':' + msg.description);
        this.dispatchEvent(new ErrorEvent(msg.code, msg.description));
        break;

      default:
        break;
    }
    this.startHeartBeat();
  }

  async sendMessage(req) {
    const MAX_FRAME_SIZE = 30000; //! 30K (the AWS hard limit is 32K)

    if (this.ws) {
      let msg = JSON.stringify(req);

      if (MAX_FRAME_SIZE < msg.length) {
        const frameid = this.meetingId + ':' + g_localUserId + ':' + new Date().getTime();
        let chunk_count = Math.ceil(msg.length / MAX_FRAME_SIZE);
        for (let chunk_index = 0; chunk_index < chunk_count; ++chunk_index) {
          const payload = (chunk_index === chunk_count-1) ? msg.substr(chunk_index*MAX_FRAME_SIZE) : msg.substr(chunk_index*MAX_FRAME_SIZE, MAX_FRAME_SIZE);
          const chunk = {
            category: 'chunk',
            type: 'chunk',
            frameid,
            chunk_index,
            chunk_count,
            payload,
          };
          log(`Sending chunk ${chunk_index+1}/${chunk_count}`);
          this.ws.send(JSON.stringify(chunk));
          await sleep(30);
        }
      } else {
        log('Sending ' + (req.type || req.category));
        this.ws.send(msg);
      }
    }
  }

}

function getWhiteboard({canvasId}) {
  if (!g_whiteboardCache[canvasId]) {
    g_whiteboardCache[canvasId] = new Whiteboard({canvasId});
  }
  return g_whiteboardCache[canvasId];
}

export { getWhiteboard, MIXED_VALUE };



